/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { type Message } from '@langchain/langgraph-sdk'
import { useStream } from '@langchain/langgraph-sdk/react'
import { uiMessageReducer, type RemoveUIMessage, type UIMessage } from '@langchain/langgraph-sdk/react-ui'
import { t } from '@lingui/macro'
import { OpenAPI } from '@om1/falcon-api'
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { StringParam, useQueryParam } from 'use-query-params'
import { useThreads } from './Thread'

export type StateType = { messages: Message[]; ui?: UIMessage[] }

const useTypedStream = useStream<
    StateType,
    {
        UpdateType: {
            messages?: Message[] | Message | string
            ui?: (UIMessage | RemoveUIMessage)[] | UIMessage | RemoveUIMessage
        }
        CustomEventType: UIMessage | RemoveUIMessage
    }
>

type StreamContextType = ReturnType<typeof useTypedStream> & {
    currentRunId: string | null
}

const StreamContext = createContext<StreamContextType | undefined>(undefined)

async function sleep(ms = 4000) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

async function checkGraphStatus(apiUrl: string, authToken: string | null): Promise<boolean> {
    try {
        const res = await fetch(`${apiUrl}/info`, {
            ...(authToken && {
                headers: {
                    Authorization: `Bearer ${authToken}`
                }
            })
        })

        return res.ok
    } catch (e) {
        console.error(e)
        return false
    }
}

const StreamSession = ({
    children,
    apiUrl,
    assistantId,
    token
}: {
    children: ReactNode
    apiUrl: string
    assistantId: string
    token: string | null
}) => {
    const [threadId, setThreadId] = useQueryParam('threadId', StringParam)
    const [currentRunId, setCurrentRunId] = useState<string | null>(null)
    const { getThreads, setThreads } = useThreads()

    // Memoize the metadata event handler to prevent recreation on every render
    const handleMetadataEvent = useCallback((metadata: any) => {
        if (metadata && typeof metadata === 'object' && 'run_id' in metadata) {
            setCurrentRunId(metadata.run_id as string)
        }
    }, [])

    const streamValue = useTypedStream({
        apiUrl,
        defaultHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
        assistantId,
        threadId: threadId || null,
        onCustomEvent: (event, options) => {
            options.mutate((prev) => {
                const ui = uiMessageReducer(prev.ui ?? [], event)
                return { ...prev, ui }
            })
        },
        onThreadId: (id) => {
            setThreadId(id, 'pushIn')
            // Refetch threads list when thread ID changes.
            // Wait for some seconds before fetching so we're able to get the new thread that was created.
            // Using getThreads to ensure we show threads from all assistants
            sleep().then(() => getThreads().then(setThreads).catch(console.error))
        },
        onMetadataEvent: handleMetadataEvent
    })

    // Clear run_id when not loading
    useEffect(() => {
        if (!streamValue.isLoading) {
            setCurrentRunId(null)
        }
    }, [streamValue.isLoading])

    useEffect(() => {
        if (token) {
            checkGraphStatus(apiUrl, token).then((ok) => {
                if (!ok) {
                    toast.error(t`Failed to connect to LangGraph server`, {
                        description: () => (
                            <p>
                                Please ensure your graph is running at <code>{apiUrl}</code> and you are properly authenticated.
                            </p>
                        ),
                        duration: 10000,
                        richColors: true,
                        closeButton: true
                    })
                }
            })
        }
    }, [token, apiUrl])

    // Memoize the enhanced stream value to prevent unnecessary re-renders
    const enhancedStreamValue = useMemo(
        () => ({
            ...streamValue,
            currentRunId
        }),
        [streamValue, currentRunId]
    )

    return <StreamContext.Provider value={enhancedStreamValue}>{children}</StreamContext.Provider>
}

const DEFAULT_API_URL = process.env.REACT_APP_FALCON_AGENT_API_URL as string
const DEFAULT_ASSISTANT_ID = 'sttm'

export const StreamProvider: React.FC<{
    children: ReactNode
    assistantId: string
    apiUrl: string
}> = ({ children, assistantId, apiUrl }) => {
    const token = OpenAPI.TOKEN ? String(OpenAPI.TOKEN) : null
    const finalApiUrl = apiUrl || DEFAULT_API_URL
    const finalAssistantId = assistantId || DEFAULT_ASSISTANT_ID

    return (
        <StreamSession apiUrl={finalApiUrl} assistantId={finalAssistantId} token={token}>
            {children}
        </StreamSession>
    )
}

// Create a custom hook to use the context
export const useStreamContext = (): StreamContextType => {
    const context = useContext(StreamContext)
    if (context === undefined) {
        throw new Error(t`No thread found in context. Please ensure you are using the StreamProvider.`)
    }
    return context
}

export default StreamContext
